import { outcomeMarketCapAtPriceD6 } from '@morfi/market-math';
import { addD6String } from './d6.js';
import { effectiveMcapD6 } from './effective-mcap.js';
import { readIntegerString, readIntegerStringAt } from './live-bucket-core.js';

interface CanonicalOutcomeLike {
  readonly mint?: string;
  readonly market?: string;
  readonly outcomeIndex?: number;
  readonly price?: string;
  readonly virtualQuoteD6?: string;
  readonly latestPoolQuoteD6?: string;
  readonly cpQuoteReserveD6?: string;
  readonly indexedOutcomeMintSupplyD6?: string;
  readonly effectiveMarketCapD6?: string;
  readonly conviction?: string;
  readonly convictionAnchor?: string;
  readonly convictionTarget?: string;
  readonly volumeTotal?: string;
  readonly graduated?: boolean;
  readonly change15mBps?: string | null;
  readonly change1hBps?: string | null;
  readonly change24hBps?: string | null;
}

const BPS = 10_000n;

function positiveInteger(raw: Record<string, unknown>, field: string): string | null {
  const value = readIntegerString(raw, field);
  return value !== null && BigInt(value) > 0n ? value : null;
}

/** Apply one bonding-curve trade directly to its mint entity. The execution
 * price field is the rolling-deploy fence: legacy payloads carried execution
 * average in `postOutcomePriceD9`, so they must not overwrite the spot mark. */
export function applyMarketTradeToOutcome<T extends CanonicalOutcomeLike>(
  prev: T,
  raw: Record<string, unknown>,
  _args: Record<string, unknown>,
  subjectMarket?: string,
): T {
  if (subjectMarket === undefined || prev.market !== subjectMarket) return prev;
  const outcomeIndex = readIntegerString(raw, 'outcomeIdx');
  if (outcomeIndex === null || Number(outcomeIndex) !== prev.outcomeIndex) return prev;

  const executionPrice = positiveInteger(raw, 'executionPriceD9');
  const price = executionPrice === null ? null : positiveInteger(raw, 'postOutcomePriceD9');
  const latestPoolQuoteD6 = readIntegerString(raw, 'postBcQuoteReservesD6');
  const conviction = readIntegerStringAt(
    raw,
    'postOutcomeConvictionPpb',
    prev.outcomeIndex ?? Number(outcomeIndex),
  );
  const convictionTarget = readIntegerStringAt(
    raw,
    'postOutcomeConvictionTargetPpb',
    prev.outcomeIndex ?? Number(outcomeIndex),
  );
  let effectiveMarketCapD6: string | undefined;
  if (latestPoolQuoteD6 !== null && prev.virtualQuoteD6 !== undefined) {
    const virtual = BigInt(prev.virtualQuoteD6);
    const computed = virtual > 0n ? effectiveMcapD6(BigInt(latestPoolQuoteD6), virtual) : null;
    effectiveMarketCapD6 = computed?.toString();
  }

  const next = {
    ...(price !== null && price !== prev.price ? { price } : {}),
    ...(latestPoolQuoteD6 !== null && latestPoolQuoteD6 !== prev.latestPoolQuoteD6
      ? { latestPoolQuoteD6 }
      : {}),
    ...(effectiveMarketCapD6 !== undefined && effectiveMarketCapD6 !== prev.effectiveMarketCapD6
      ? { effectiveMarketCapD6 }
      : {}),
    ...(conviction !== null && conviction !== prev.conviction
      ? { conviction, convictionAnchor: conviction }
      : {}),
    ...(convictionTarget !== null && convictionTarget !== prev.convictionTarget
      ? { convictionTarget }
      : {}),
  };
  return Object.keys(next).length === 0 ? prev : ({ ...prev, ...next } as T);
}

/** Apply one CP-swap activity event directly to its mint entity. */
export function applyAmmActivityToOutcome<T extends CanonicalOutcomeLike>(
  prev: T,
  raw: Record<string, unknown>,
  _args: Record<string, unknown>,
  subjectMint?: string,
): T {
  if (subjectMint === undefined || prev.mint !== subjectMint) return prev;
  const price = positiveInteger(raw, 'postOutcomePriceD9');
  const cpQuoteReserveD6 = readIntegerString(raw, 'postQuoteReservesD6');
  const amountD6 = readIntegerString(raw, 'amountD6');
  const volumeTotal = amountD6 === null ? undefined : addD6String(prev.volumeTotal, amountD6);
  let effectiveMarketCapD6: string | undefined;
  let change15mBps: string | undefined;
  let change1hBps: string | undefined;
  let change24hBps: string | undefined;
  if (price !== null && price !== prev.price && prev.price !== undefined) {
    const previousPrice = BigInt(prev.price);
    const nextPrice = BigInt(price);
    if (prev.indexedOutcomeMintSupplyD6 !== undefined) {
      const result = outcomeMarketCapAtPriceD6({
        poolModel: 'cp_swap',
        priceD9: nextPrice,
        indexedOutcomeMintSupplyD6: BigInt(prev.indexedOutcomeMintSupplyD6),
      });
      effectiveMarketCapD6 = result.available ? result.value.toString() : undefined;
    } else if (previousPrice > 0n && prev.effectiveMarketCapD6 !== undefined) {
      effectiveMarketCapD6 = (
        (BigInt(prev.effectiveMarketCapD6) * nextPrice) /
        previousPrice
      ).toString();
    }
    if (previousPrice > 0n) {
      change15mBps = repriceChangeBps(prev.change15mBps, previousPrice, nextPrice);
      change1hBps = repriceChangeBps(prev.change1hBps, previousPrice, nextPrice);
      change24hBps = repriceChangeBps(prev.change24hBps, previousPrice, nextPrice);
    }
  }
  const next = {
    ...(price !== null && price !== prev.price ? { price } : {}),
    ...(cpQuoteReserveD6 !== null && cpQuoteReserveD6 !== prev.cpQuoteReserveD6
      ? { cpQuoteReserveD6 }
      : {}),
    ...(volumeTotal !== undefined && volumeTotal !== prev.volumeTotal ? { volumeTotal } : {}),
    ...(effectiveMarketCapD6 !== undefined && effectiveMarketCapD6 !== prev.effectiveMarketCapD6
      ? { effectiveMarketCapD6 }
      : {}),
    ...(change15mBps !== undefined ? { change15mBps } : {}),
    ...(change1hBps !== undefined ? { change1hBps } : {}),
    ...(change24hBps !== undefined ? { change24hBps } : {}),
    ...(prev.graduated === true ? {} : { graduated: true }),
  };
  return Object.keys(next).length === 0 ? prev : ({ ...prev, ...next } as T);
}

function repriceChangeBps(
  previousBps: string | null | undefined,
  previousPrice: bigint,
  nextPrice: bigint,
): string | undefined {
  if (previousBps === null || previousBps === undefined) return undefined;
  const next = (nextPrice * (BPS + BigInt(previousBps))) / previousPrice - BPS;
  const value = next.toString();
  return value === previousBps ? undefined : value;
}
